/**
 * Localization runtime helpers (ENG-387).
 *
 * The catalogs under `src/locales/<tag>/common.json` follow the i18next JSON
 * conventions, so a later move to i18next would swap this module without
 * rewriting a single message (the ENG-378 bootstrap, `src/locales/bootstrap.js`,
 * and the formatters, `formatters.js`, sit beside it and would stay):
 *
 *   - nested objects, `.`-joined into keys (`navigation.links.profile`)
 *   - `{{name}}` interpolation, plain text only (React escapes on render)
 *   - plural forms as `key_one` / `key_other` (…`_zero`, `_two`, `_few`,
 *     `_many` where the locale needs them), selected with `params.count`
 *     through `Intl.PluralRules`
 *
 * Missing keys fall back per key to the English source catalog and are
 * reported through `reportMissingKey` with only the locale and the key —
 * never the message, the interpolation parameters or anything a user typed.
 */

export const PLURAL_CATEGORIES = Object.freeze(['zero', 'one', 'two', 'few', 'many', 'other']);

/** `key_one` → plural form `one` of `key`. */
export const PLURAL_SUFFIX = /_(zero|one|two|few|many|other)$/;

/** `{{name}}`, optional inner whitespace; `name` follows the key-segment rules. */
export const INTERPOLATION = /\{\{\s*([^{}\s]+)\s*\}\}/g;

/**
 * Nested catalog → flat `{ 'a.b.c': 'message' }`. Leaves are kept as-is
 * (the catalog gate rejects anything that is not a non-empty string).
 */
export const flattenCatalog = (tree, prefix = '') =>
  Object.entries(tree ?? {}).reduce((flat, [segment, value]) => {
    const key = prefix ? `${prefix}.${segment}` : segment;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(flat, flattenCatalog(value, key));
    } else {
      flat[key] = value;
    }
    return flat;
  }, {});

/** Sorted, de-duplicated interpolation variable names of a message. */
export const placeholders = (message) =>
  Array.from(new Set(Array.from(String(message).matchAll(INTERPOLATION), ([, name]) => name))).sort();

/** The plural base key of `key_one` etc., or the key itself. */
export const pluralBase = (key) => key.replace(PLURAL_SUFFIX, '');

/**
 * Substitute `{{name}}` with `params[name]`. Unknown or null parameters keep
 * the placeholder so the gap is visible instead of silently blank.
 */
export const interpolate = (message, params = {}) =>
  String(message).replace(INTERPOLATION, (match, name) =>
    params[name] === undefined || params[name] === null ? match : String(params[name])
  );

export const pluralCategory = (locale, count) => {
  try {
    return new Intl.PluralRules(locale).select(count);
  } catch {
    return 'other';
  }
};

/** Plural categories a locale can produce (`Intl.PluralRules`, cardinal). */
export const pluralCategoriesOf = (locale) => {
  try {
    return [...new Intl.PluralRules(locale).resolvedOptions().pluralCategories].sort();
  } catch {
    return ['other'];
  }
};

/**
 * Message for `key` in a flat catalog, honouring plural forms when `count`
 * is a number: `key_<category>`, then `key_other`, then `key`.
 */
export const lookupMessage = (messages, key, count, pluralLocale) => {
  if (!messages) return undefined;
  if (typeof count === 'number' && Number.isFinite(count)) {
    const exact = messages[`${key}_${pluralCategory(pluralLocale, count)}`];
    if (exact !== undefined) return exact;
    if (messages[`${key}_other`] !== undefined) return messages[`${key}_other`];
  }
  return messages[key];
};

/**
 * `t(key, params)` bound to one locale. Every miss in `messages` is reported
 * and served from `fallbackMessages` (English); a miss there too returns the
 * key, which the source-key gate keeps out of the tree.
 *
 * `t.has(key, params)` says whether the *active* locale has a message for
 * the key (the plural form `params.count` would select), without the English
 * fallback and without reporting a miss — the i18next `exists` counterpart,
 * for callers that hold a better fallback than English (a notification's
 * backend-rendered title, ENG-382).
 */
export const createTranslator = ({
  locale,
  messages,
  fallbackMessages,
  pluralLocale = locale,
  fallbackLocale = 'en-US',
  onMissing = () => {},
}) => {
  const countOf = (params) => (typeof params.count === 'number' ? params.count : undefined);
  const t = (key, params = {}) => {
    const count = countOf(params);
    let message = lookupMessage(messages, key, count, pluralLocale);
    if (message === undefined) {
      onMissing({ locale, key });
      message = lookupMessage(fallbackMessages, key, count, fallbackLocale);
    }
    if (message === undefined) {
      if (fallbackLocale !== locale) onMissing({ locale: fallbackLocale, key });
      return key;
    }
    return interpolate(message, params);
  };
  t.has = (key, params = {}) => lookupMessage(messages, key, countOf(params), pluralLocale) !== undefined;
  return t;
};

/* ── Missing-key telemetry ─────────────────────────────────────────────── */

const missingKeyReporters = new Set();
const reportedMissingKeys = new Set();

/**
 * Register a production sink for missing keys. The event is
 * `{ locale, key, namespace }` and nothing else, so it is safe to ship to
 * monitoring without a content review. Returns an unsubscribe function.
 */
export const onMissingKey = (reporter) => {
  missingKeyReporters.add(reporter);
  return () => missingKeyReporters.delete(reporter);
};

/** Forget which keys were already reported (tests). */
export const resetMissingKeyTelemetry = () => {
  reportedMissingKeys.clear();
};

/** Locale + key only, de-duplicated per page load; warns in dev when no sink is registered. */
export const reportMissingKey = ({ locale, key }) => {
  const id = `${locale}\u0000${key}`;
  if (reportedMissingKeys.has(id)) return;
  reportedMissingKeys.add(id);
  const event = Object.freeze({ locale, key, namespace: String(key).split('.')[0] });
  if (missingKeyReporters.size === 0) {
    if (import.meta.env.DEV) console.warn(`[i18n] missing key "${event.key}" for ${event.locale}`);
    return;
  }
  for (const reporter of missingKeyReporters) {
    try {
      reporter(event);
    } catch {
      // A broken sink must never take the UI down with it.
    }
  }
};

/* ── Pseudo-localization ───────────────────────────────────────────────── */

const ACCENTED = {
  a: 'á', b: 'ƀ', c: 'ç', d: 'ð', e: 'é', f: 'ƒ', g: 'ģ', h: 'ĥ', i: 'í', j: 'ĵ', k: 'ķ', l: 'ļ', m: 'ɱ',
  n: 'ñ', o: 'ó', p: 'þ', q: 'ǫ', r: 'ŕ', s: 'š', t: 'ŧ', u: 'ú', v: 'ṽ', w: 'ŵ', x: 'ẋ', y: 'ý', z: 'ž',
  A: 'Á', B: 'Ɓ', C: 'Ç', D: 'Ð', E: 'É', F: 'Ƒ', G: 'Ģ', H: 'Ĥ', I: 'Í', J: 'Ĵ', K: 'Ķ', L: 'Ļ', M: 'Ṁ',
  N: 'Ñ', O: 'Ó', P: 'Þ', Q: 'Ǫ', R: 'Ŕ', S: 'Š', T: 'Ŧ', U: 'Ú', V: 'Ṿ', W: 'Ŵ', X: 'Ẋ', Y: 'Ý', Z: 'Ž',
};

/** Longest translations run ~35% over English (German, Finnish); pad to that. */
export const PSEUDO_EXPANSION_RATIO = 0.35;
export const PSEUDO_PAD_CHAR = '~';
export const RLO = '\u202E'; // RIGHT-TO-LEFT OVERRIDE
export const PDF = '\u202C'; // POP DIRECTIONAL FORMATTING

const accent = (text) => text.replace(/[A-Za-z]/g, (letter) => ACCENTED[letter] ?? letter);

/**
 * Pseudo-localize one message, leaving `{{placeholders}}` intact.
 *
 *   expand: `Performers` → `[Þéŕƒóŕɱéŕš~~~]` — every ASCII letter is replaced
 *           so any plain English left on screen is a hard-coded string, and
 *           the padding shows what a long translation does to the layout.
 *   rtl:    each text run is wrapped in RLO…PDF so Latin glyphs render
 *           right-to-left, exercising mirroring without a real translation.
 */
export const pseudoLocalize = (message, { rtl = false } = {}) => {
  const parts = String(message).split(/(\{\{[^{}]*\}\})/);
  const transformed = parts
    .map((part, index) => {
      if (index % 2 === 1 || part === '') return part; // placeholder or empty run
      return rtl ? `${RLO}${part}${PDF}` : accent(part);
    })
    .join('');
  if (rtl) return transformed;
  const letters = (String(message).match(/[A-Za-z]/g) ?? []).length;
  return `[${transformed}${PSEUDO_PAD_CHAR.repeat(Math.ceil(letters * PSEUDO_EXPANSION_RATIO))}]`;
};

export const pseudoLocalizeCatalog = (messages, options) =>
  Object.fromEntries(Object.entries(messages).map(([key, message]) => [key, pseudoLocalize(message, options)]));

/**
 * Under the expanding pseudo-locale nothing rendered from the catalog keeps
 * an ASCII letter, so any that remain came from a hard-coded string (or from
 * user content, which callers exclude before asking).
 */
export const looksHardCoded = (text) => /[A-Za-z]/.test(text);
